import { HttpService } from './client';
import {
  AlreadyAcceptedInlineHandle,
  ExistingSignatureInlineHandle,
  InlineClickwrapHandle,
} from './inline/handles';
import { invokeInlineLifecycleCallback } from './inline/lifecycle';
import { inlineRegistry } from './inline/registry';
import {
  ActionStatus,
  ClicktermDialogConfig,
  ClicktermInlineFinalizeAllResult,
  ClicktermDialogTranslations,
  ClicktermDialogTranslationsMap,
  ClicktermInlineHandle,
  ClicktermInlineOptions,
  ClicktermInlineReadyInfo,
  ClickwrapAcceptedAgreementRequest,
  ClickwrapButtonSettingsOptions,
  ClickwrapCustomizationSettingsResponse,
  ClickwrapCustomizationSettingsResult,

  ClickwrapResult,
  ClickwrapTemplateContent,
  ClickwrapTemplateRequest,
  ClickwrapTemplateResponse,
  ClickwrapThemeColorsOptions,
} from './models';
import { Renderer } from './ui/renderer';
import { getDialogTranslations } from './translations';
import { ClickwrapError } from './utils';

export class Widget {
  private readonly service: HttpService;
  private readonly renderer: Renderer;

  constructor(appId: string, baseUrl: string) {
    if (!appId) {
      throw new ClickwrapError('AppId is required');
    }
    this.service = new HttpService(baseUrl, {}, { appId });
    this.renderer = new Renderer(document, appId);
  }

  async clickwrap(
    options: ClickwrapTemplateRequest,
    dialogOptions: ClicktermDialogConfig = {},
  ): Promise<ClickwrapResult> {
    const templatePlaceholders = options.templatePlaceholders ?? null;
    try {
      const cwp = await this.service.clickwrap({
        endUserId: options.endUserId,
        clickwrapTemplateId: options.clickwrapTemplateId,
        templatePlaceholders,
        language: options.language ?? 'en',
        interactionType: options.interactionType,
      });
      if (this.isAlreadyAcceptedResponse(cwp)) {
        return Promise.resolve({
          clicktermSignature: null,
          isAlreadyAccepted: true,
          isCanceled: false,
        });
      }
      if (cwp.content != null) {
        return this.handleContent(cwp, options, dialogOptions);
      }
      return Promise.resolve({
        clicktermSignature: cwp.clicktermSignature,
        isAlreadyAccepted: false,
        isCanceled: false,
      });
    } catch (error) {
      return Promise.reject(this.normalizeError('Failed clickwrap request', error));
    }
  }

  async acceptedContent(
    options: ClickwrapAcceptedAgreementRequest,
    _: ClicktermDialogConfig = {},
  ): Promise<ClickwrapTemplateContent> {
    try {
      const [content, customizations] = await Promise.all([
        this.service.getAcceptedContent({
          endUserId: options.endUserId,
          clickwrapTemplateId: options.clickwrapTemplateId,
        }),
        this.loadCustomizations(options.clickwrapTemplateId),
      ]);
      const baseTranslations = await getDialogTranslations(content.resolvedLanguage);
      const dialogTranslations = this.mergeDialogTranslations(baseTranslations, content.resolvedLanguage, customizations);
      const modal = this.renderer.createReadOnlyModal(content, dialogTranslations, customizations ?? undefined);
      const okButton = modal.root.querySelector('.ct-w-btn.btn-accept') as HTMLButtonElement | null;
      if (!okButton) {
        throw new ClickwrapError('Failed to render accepted content modal.');
      }
      return new Promise<ClickwrapTemplateContent>((resolve) => {
        okButton.addEventListener('click', async () => {
          okButton.setAttribute('disabled', 'disabled');
          okButton.classList.add('loading');
          await modal.close();
          resolve(content);
        });
      });
    } catch (error) {
      throw this.normalizeError('Failed to display accepted clickwrap content', error);
    }
  }

  async renderInline(
    containerId: string,
    options: ClickwrapTemplateRequest,
    inlineOptions: ClicktermInlineOptions = {},
  ): Promise<ClicktermInlineHandle> {
    const normalizedContainerId = containerId.trim();
    if (!normalizedContainerId) {
      throw new ClickwrapError('Container ID is required');
    }

    const container = document.getElementById(normalizedContainerId);
    if (!container) {
      throw new ClickwrapError(`Container element "${normalizedContainerId}" was not found.`);
    }

    const existingEntry = inlineRegistry.getCurrentEntry(normalizedContainerId);
    if (existingEntry) {
      throw new ClickwrapError(`Inline clickwrap already rendered or rendering for container "${normalizedContainerId}".`);
    }

    const templatePlaceholders = options.templatePlaceholders ?? null;
    const reservation = inlineRegistry.reserve(normalizedContainerId, container);
    try {
      invokeInlineLifecycleCallback('onLoading', () => inlineOptions.onLoading?.({ containerId: normalizedContainerId }));
      const cwp = await this.service.clickwrap({
        endUserId: options.endUserId,
        clickwrapTemplateId: options.clickwrapTemplateId,
        templatePlaceholders,
        language: options.language ?? 'en',
        interactionType: options.interactionType,
      });
      if (this.isAlreadyAcceptedResponse(cwp)) {
        const handle = this.registerInlineHandle(
          normalizedContainerId,
          container,
          reservation,
          (onDestroy) => new AlreadyAcceptedInlineHandle(onDestroy),
        );
        this.notifyInlineReady(inlineOptions, {
          containerId: normalizedContainerId,
          outcome: 'ALREADY_ACCEPTED',
          clicktermSignature: null,
          handle,
        });
        return handle;
      }

      if (cwp.content) {
        const content = cwp.content;
        if (!content.inlineContent) {
          throw new ClickwrapError('Inline clickwrap response is missing inlineContent.');
        }
        const [baseTranslations, customizations] = await Promise.all([
          getDialogTranslations(content.resolvedLanguage),
          this.loadCustomizations(options.clickwrapTemplateId),
        ]);
        const dialogTranslations = this.mergeDialogTranslations(baseTranslations, content.resolvedLanguage, customizations);
        const view = this.renderer.createInline(container, content, inlineOptions.style, {
          translations: dialogTranslations,
          customizations: customizations ?? undefined,
        });
        const handle = this.registerInlineHandle(normalizedContainerId, container, reservation, (onDestroy) => new InlineClickwrapHandle({
          service: this.service,
          view,
          content,
          endUserId: options.endUserId,
          interactionType: options.interactionType,
          onChange: inlineOptions.onChange,
          onDestroy,
        }));
        this.notifyInlineReady(inlineOptions, {
          containerId: normalizedContainerId,
          outcome: 'RENDERED',
          clicktermSignature: null,
          handle,
        });
        return handle;
      }

      if (cwp.clicktermSignature) {
        const clicktermSignature = cwp.clicktermSignature;
        const handle = this.registerInlineHandle(
          normalizedContainerId,
          container,
          reservation,
          (onDestroy) => new ExistingSignatureInlineHandle(clicktermSignature, onDestroy),
        );
        this.notifyInlineReady(inlineOptions, {
          containerId: normalizedContainerId,
          outcome: 'EXISTING_SIGNATURE',
          clicktermSignature,
          handle,
        });
        return handle;
      }

      throw new ClickwrapError('Inline clickwrap response did not include renderable content.');
    } catch (error) {
      inlineRegistry.unregister(normalizedContainerId, reservation);
      // onError stays owned by the public ClicktermDom boundary so it fires exactly once.
      throw this.normalizeError('Failed inline clickwrap request', error);
    }
  }

  private notifyInlineReady(inlineOptions: ClicktermInlineOptions, info: ClicktermInlineReadyInfo): void {
    invokeInlineLifecycleCallback('onReady', () => inlineOptions.onReady?.(info));
  }

  async finalizeAll(containerIds?: string[]): Promise<ClicktermInlineFinalizeAllResult> {
    const requestedContainerIds = containerIds == null
      ? inlineRegistry.getContainerIds()
      : Array.from(new Set(containerIds.map((containerId) => containerId.trim())));

    if (requestedContainerIds.length === 0) {
      throw new ClickwrapError('No inline clickwraps are available to finalize.');
    }

    const emptyContainerId = requestedContainerIds.find((containerId) => !containerId);
    if (emptyContainerId !== undefined) {
      throw new ClickwrapError('Container ID is required');
    }

    const handles = new Map<string, ClicktermInlineHandle>();
    const missingContainerIds = requestedContainerIds.filter((containerId) => {
      const handle = inlineRegistry.getHandle(containerId);
      if (!handle) {
        return true;
      }
      handles.set(containerId, handle);
      return false;
    });
    if (missingContainerIds.length > 0) {
      const names = missingContainerIds.map((containerId) => `"${containerId}"`).join(', ');
      throw new ClickwrapError(`Inline clickwrap was not rendered for container(s): ${names}.`);
    }

    try {
      const entries = await Promise.all(requestedContainerIds.map(async (containerId) => {
        const handle = handles.get(containerId);
        if (!handle) {
          throw new ClickwrapError(`Inline clickwrap was not rendered for container "${containerId}".`);
        }
        return [containerId, await handle.finalize()] as const;
      }));
      return Object.fromEntries(entries);
    } catch (error) {
      throw this.normalizeError('Failed to finalize inline clickwraps', error);
    }
  }

  private registerInlineHandle(
    containerId: string,
    container: HTMLElement,
    reservation: symbol,
    createHandle: (onDestroy: () => void) => ClicktermInlineHandle,
  ): ClicktermInlineHandle {
    let handle!: ClicktermInlineHandle;
    const onDestroy = (): void => {
      const entry = inlineRegistry.getEntry(containerId);
      if (entry?.state === 'active' && entry.handle === handle) {
        inlineRegistry.unregister(containerId);
      }
    };

    handle = createHandle(onDestroy);
    if (!inlineRegistry.register(containerId, container, handle, reservation)) {
      handle.destroy?.();
      throw new ClickwrapError(`Inline clickwrap render for container "${containerId}" could not be completed.`);
    }

    return handle;
  }

  private async handleContent(
    cwp: ClickwrapTemplateResponse,
    options: ClickwrapTemplateRequest,
    dialogOptions: ClicktermDialogConfig,
  ): Promise<ClickwrapResult> {
    const resolvedLanguage = cwp.content?.resolvedLanguage || options.language;
    const [baseTranslations, customizations] = await Promise.all([
      getDialogTranslations(resolvedLanguage),
      this.loadCustomizations(options.clickwrapTemplateId),
    ]);
    const dialogTranslations = this.mergeDialogTranslations(baseTranslations, resolvedLanguage, customizations);
    const modal = this.renderer.createModal(cwp.content!, dialogTranslations, customizations ?? undefined);
    const disableClose = dialogOptions.disableClose ?? false;
    return new Promise<ClickwrapResult>((resolve, reject) => {
      const buttons = modal.root.querySelectorAll('.ct-w-btn');
      if (!disableClose) {
        modal.root.addEventListener('click', (e) => {
          if (e.target === modal.root) {
            modal.close();
            e.stopPropagation();
            resolve({ isCanceled: true, isAlreadyAccepted: false, clicktermSignature: null });
          }
        });
      }
      Renderer.onAll(buttons, 'click', async (e: Event) => {
        const btn = e.currentTarget as HTMLElement;
        const actionStatus: ActionStatus = btn.classList.contains('btn-decline')
          ? 'DECLINED'
          : 'ACCEPTED';
        buttons.forEach((button) => button.setAttribute('disabled', 'disabled'));
        btn.classList.add('loading');
        try {
          const signature = await this.service.applyAgreement({
            clickwrapTemplateId: cwp.content!.clickwrapTemplateId,
            clickwrapTemplateVersion: cwp.content!.clickwrapTemplateVersion,
            clickwrapTemplateVersionMinor: cwp.content!.clickwrapTemplateVersionMinor,
            endUserId: options.endUserId,
            clickwrapEventStatus: actionStatus,
            interactionType: options.interactionType,
          });
          await modal.close();
          resolve({
            clicktermSignature: signature.clicktermSignature,
            isAlreadyAccepted: false,
            isCanceled: false,
          });
        } catch (error) {
          await modal.close();
          reject(this.normalizeError('Failed to send event', error));
        }
      });
    });
  }

  private isAlreadyAcceptedResponse(response: ClickwrapTemplateResponse): boolean {
    return response.clicktermSignature == null && response.content == null;
  }

  private async loadCustomizations(clickwrapTemplateId: string): Promise<ClickwrapCustomizationSettingsResult | null> {
    try {
      const response = await this.service.getCustomizations(clickwrapTemplateId);
      if (!response) {
        return null;
      }
      return this.normalizeCustomizations(response);
    } catch (error) {
      console.warn('Failed to load clickwrap customizations.', error);
      return null;
    }
  }

  private normalizeCustomizations(settings: ClickwrapCustomizationSettingsResponse): ClickwrapCustomizationSettingsResult {
    return {
      clickwrapTemplateId: settings.clickwrapTemplateId,
      agreementMode: this.normalizeAgreementMode(settings.agreementMode),
      isLogoCustomizable: Boolean(settings.isLogoCustomizable),
      logoUrl: settings.logoUrl ?? null,
      themeColors: this.parseCustomizationJson<ClickwrapThemeColorsOptions>(settings.themeColors),
      buttonSettings: this.parseCustomizationJson<ClickwrapButtonSettingsOptions>(settings.buttonSettings),
      textCustomizations: this.parseTextCustomizations(settings.textCustomizations),
    };
  }

  private mergeDialogTranslations(
    baseTranslations: ClicktermDialogTranslations,
    language?: string,
    customizations?: ClickwrapCustomizationSettingsResult | null,
  ): ClicktermDialogTranslations {
    const overrides = this.resolveTextCustomizationForLanguage(customizations?.textCustomizations, language);
    if (!overrides) {
      return baseTranslations;
    }
    return {
      ...baseTranslations,
      ...overrides,
    };
  }

  private resolveTextCustomizationForLanguage(
    textCustomizations?: ClicktermDialogTranslationsMap,
    language?: string,
  ): Partial<ClicktermDialogTranslations> | undefined {
    if (!textCustomizations) {
      return undefined;
    }
    const normalized = language?.trim().toLowerCase();
    if (!normalized) {
      return undefined;
    }
    return textCustomizations[normalized];
  }

  private normalizeAgreementMode(mode?: string | null): 'scroll' | 'checkbox' | undefined {
    if (!mode) {
      return undefined;
    }
    const normalized = mode.trim().toLowerCase();
    if (normalized === 'scroll') {
      return 'scroll';
    }
    if (normalized === 'checkbox') {
      return 'checkbox';
    }
    return undefined;
  }

  private parseTextCustomizations(value?: string | null): ClicktermDialogTranslationsMap | undefined {
    const parsed = this.parseCustomizationJson<unknown>(value);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return undefined;
    }

    const result: ClicktermDialogTranslationsMap = {};
    const entries = Object.entries(parsed as Record<string, unknown>);
    for (const [language, entry] of entries) {
      if (!entry || typeof entry !== 'object' || Array.isArray(entry)) {
        continue;
      }
      const normalizedLanguage = language.trim().toLowerCase();
      if (!normalizedLanguage) {
        continue;
      }
      const sanitizedEntry = this.sanitizeTextCustomizationEntry(entry as Record<string, unknown>);
      if (Object.keys(sanitizedEntry).length > 0) {
        result[normalizedLanguage] = sanitizedEntry;
      }
    }

    if (Object.keys(result).length === 0) {
      return undefined;
    }
    return result;
  }

  private sanitizeTextCustomizationEntry(entry: Record<string, unknown>): Partial<ClicktermDialogTranslations> {
    const result: Partial<ClicktermDialogTranslations> = {};
    const allowedKeys: Array<keyof ClicktermDialogTranslations> = [
      'publishedOn',
      'version',
      'accept',
      'decline',
      'checkboxLabelText',
      'checkboxDescription',
      'scrollTextTitle',
      'scrollTextSubtitle',
      'scroll',
      'cancel',
      'close',
      'acceptedAt',
    ];
    for (const key of allowedKeys) {
      const value = entry[key];
      if (typeof value === 'string') {
        result[key] = value;
      }
    }
    return result;
  }

  private parseCustomizationJson<T>(value?: string | null): T | undefined {
    if (!value) {
      return undefined;
    }
    if (typeof value === 'object') {
      return value as T;
    }
    try {
      return JSON.parse(value) as T;
    } catch (error) {
      console.warn('Failed to parse customization JSON.', error);
      return undefined;
    }
  }

  private normalizeError(message: string, error: unknown): ClickwrapError {
    if (error instanceof ClickwrapError) {
      return error;
    }
    const reason = error instanceof Error ? error.message : 'Unknown error';
    return new ClickwrapError(`${message}: ${reason}`, error);
  }
}
